/**
 * Sidebar com informações do grupo WhatsApp
 * 
 * Exibe: nome, descrição, avatar, participantes, estatísticas.
 * Permite sincronizar lista de participantes via provider-proxy.
 * Utiliza cache para evitar chamadas repetidas à API.
 * Inclui configuração de distribuição automática de conversas.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  X,
  Users,
  Calendar,
  Hash,
  RefreshCw,
  Loader2,
  Shield,
  Crown,
  ArrowLeft,
  Camera,
  Zap,
  Building2,
  Upload,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { ChatGroup, Conversation } from '@/pages/tenant/Chat';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getStorageUrl } from '@/lib/storage';
import { useGroupParticipantsCache } from '@/hooks/useGroupParticipantsCache';
import { useGroupDistribution, DistributionTrigger } from '@/hooks/useGroupDistribution';
import { useGroupUtils } from '@/hooks/useGroupUtils';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';

// Interface para departamentos
interface Department {
  id: string;
  name: string;
}

interface GroupSidebarProps {
  group: ChatGroup;
  conversation: Conversation;
  onClose: () => void;
}

interface GroupParticipant {
  id: string;
  participant_phone: string;
  display_name: string | null;
  is_admin: boolean;
  is_super_admin: boolean;
  joined_at: string;
}

export function GroupSidebar({ group, conversation, onClose }: GroupSidebarProps) {
  const { currentTenant } = useAuth();
  
  // Hook de cache para evitar chamadas repetidas à API
  const { fetchParticipants } = useGroupParticipantsCache();
  
  // Hook de distribuição do grupo
  const { config, loading: loadingConfig, saving, updateConfig } = useGroupDistribution(group.id);
  
  // Hook de utilitários de grupos (multi-provider)
  const { setGroupPicture } = useGroupUtils();
  
  const [participants, setParticipants] = useState<GroupParticipant[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [groupPhoto, setGroupPhoto] = useState<string | null>(group.avatar_url);
  // Controla expansão da descrição do grupo (colapsada por padrão)
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  // Indica se os dados vieram do cache (para feedback visual opcional)
  const [fromCache, setFromCache] = useState(false);
  // Mapa: participant_phone normalizado -> nome do contato cadastrado (CRM).
  // Carregado direto de chat_group_participants -> contacts via FK existente.
  const [contactsByPhone, setContactsByPhone] = useState<Map<string, string>>(new Map());
  
  // Lista de departamentos do tenant
  const [departments, setDepartments] = useState<Department[]>([]);

  const getInitials = (name: string | null) => {
    if (!name) return 'G';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  /**
   * Sincroniza estados locais quando o grupo selecionado muda
   * Resolve bug: informações do grupo anterior ficavam visíveis
   * ao trocar de conversa
   */
  useEffect(() => {
    // Reseta foto para o novo grupo
    setGroupPhoto(group.avatar_url);
    // Limpa lista do grupo anterior (não há mais sync automático ao trocar)
    setParticipants([]);
    setLoadingParticipants(false);
    setLoadingPhoto(false);
    setFromCache(false);
    setContactsByPhone(new Map());
  }, [group.id, group.avatar_url]);

  /**
   * Carrega o mapa "telefone do participante -> nome do contato CRM".
   * Lê chat_group_participants do grupo atual e faz JOIN automático com
   * contacts via FK (contact_id). Sem RPC nova, sem n+1.
   * Roda 1x por grupo aberto; barato porque grupos têm dezenas/centenas de itens.
   */
  useEffect(() => {
    let cancelled = false;
    const loadContacts = async () => {
      if (!group.id) return;
      const { data, error } = await supabase
        .from('chat_group_participants')
        .select('participant_phone, contacts:contact_id(name)')
        .eq('group_id', group.id);

      if (cancelled) return;
      if (error) {
        // Falha não-bloqueante: lista exibe display_name/phone como antes
        console.warn('[GroupSidebar] Falha ao carregar nomes de contatos:', error.message);
        return;
      }

      const map = new Map<string, string>();
      (data || []).forEach((row: any) => {
        const phone = (row.participant_phone || '').replace(/\D/g, '');
        const name = row.contacts?.name?.trim();
        if (phone && name) map.set(phone, name);
      });
      setContactsByPhone(map);
    };
    loadContacts();
    return () => { cancelled = true; };
  }, [group.id]);

  /**
   * Busca departamentos do tenant para configurar distribuição
   */
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!currentTenant?.id) return;
      
      const { data, error } = await supabase
        .from('departments')
        .select('id, name')
        .eq('tenant_id', currentTenant.id)
        .order('name');
      
      if (!error && data) {
        setDepartments(data);
      }
    };
    
    fetchDepartments();
  }, [currentTenant?.id]);

  /**
   * NÃO carrega participantes automaticamente ao abrir/trocar de grupo.
   *
   * Motivo: cada abertura disparava chamada ao provider (Z-API/Evolution),
   * UPDATE em chat_groups.participant_count e toast — gerando carga
   * desnecessária no banco e ruído visual em picos de uso.
   *
   * A lista agora é carregada SOMENTE quando o usuário clica no botão
   * "Atualizar participantes" (handleSyncParticipants(true)).
   * A contagem (participant_count) continua visível pois vem do banco
   * via get_chat_group_safe.
   */

  /**
   * Handlers para configuração de distribuição
   */
  const handleToggleDistribution = async (enabled: boolean) => {
    const success = await updateConfig({ autoDistributionEnabled: enabled });
    if (success) {
      toast.success(enabled ? 'Distribuição automática ativada' : 'Distribuição automática desativada');
    } else {
      toast.error('Erro ao atualizar configuração');
    }
  };

  const handleDepartmentChange = async (deptId: string) => {
    const success = await updateConfig({ 
      defaultDepartmentId: deptId === 'none' ? null : deptId 
    });
    if (success) {
      toast.success('Departamento atualizado');
    } else {
      toast.error('Erro ao atualizar departamento');
    }
  };

  const handleTriggerChange = async (trigger: string) => {
    const success = await updateConfig({ 
      distributionTrigger: trigger as DistributionTrigger 
    });
    if (success) {
      toast.success('Configuração de trigger atualizada');
    } else {
      toast.error('Erro ao atualizar trigger');
    }
  };

  /**
   * Sincroniza participantes do grupo
   * @param forceRefresh - Se true, ignora cache e busca da API
   */
  const handleSyncParticipants = async (forceRefresh: boolean = false) => {
    if (!conversation.inbox_id) return;
    
    setLoadingParticipants(true);
    try {
      const { participants: data, fromCache: cached } = await fetchParticipants(
        conversation.inbox_id,
        group.id,
        group.external_id,
        forceRefresh
      );
      
      setParticipants(data);
      setFromCache(cached);
      
      // Toast apenas quando busca da API (não do cache)
      if (!cached) {
        toast.success(`${data.length} participantes sincronizados`);
      }
    } catch (error) {
      console.error('Erro ao sincronizar participantes:', error);
      toast.error('Erro ao carregar participantes');
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  /**
   * Atualiza a foto de perfil do grupo via provider-proxy
   * Usa download_group_avatar para baixar e persistir no Supabase Storage
   * (evita expiração de 48h da URL temporária do Z-API)
   */
  const handleRefreshGroupPhoto = async () => {
    if (!conversation.inbox_id) return;
    
    setLoadingPhoto(true);
    try {
      // Chama provider-proxy com action download_group_avatar
      // Normaliza JID automaticamente e salva no Storage
      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: { 
          inbox_id: conversation.inbox_id, 
          action: 'download_group_avatar',
          group_jid: group.external_id,
          group_id: group.id // UUID para nomear arquivo no storage
        }
      });
      
      if (error) {
        console.error('[GroupSidebar] Erro ao baixar foto:', error);
        toast.error('Erro ao buscar foto do grupo');
        return;
      }
      
      const storagePath = data?.data?.storage_path;
      
      if (storagePath) {
        // Atualizar avatar_url no banco via RPC server-side (sem SELECT direto em chat_groups)
        const { error: updateError } = await supabase.rpc('update_chat_group_avatar_safe', {
          p_group_id: group.id,
          p_avatar_url: storagePath,
          p_tenant_hint: currentTenant?.id ?? null,
        });
        
        if (updateError) {
          console.error('[GroupSidebar] Erro ao salvar foto:', updateError);
          toast.error('Erro ao salvar foto do grupo');
          return;
        }
        
        // Atualiza estado local com o caminho do storage
        setGroupPhoto(storagePath);
        toast.success('Foto do grupo atualizada!');
      } else if (data?.error) {
        toast.info('Grupo não possui foto de perfil');
      } else {
        toast.info('Grupo não possui foto de perfil');
      }
    } catch (error) {
      console.error('[GroupSidebar] Erro ao atualizar foto:', error);
      toast.error('Erro ao atualizar foto do grupo');
    } finally {
      setLoadingPhoto(false);
    }
  };

  /**
   * Handler para upload e alteração da foto do grupo
   * Converte arquivo para base64 e envia via provider-proxy
   */
  const handleUploadGroupPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !conversation.inbox_id) return;
    
    // Valida tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return;
    }
    
    // Valida tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      // Converte para base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target?.result as string;
        
        try {
          // Chama setGroupPicture via hook multi-provider
          await setGroupPicture(conversation.inbox_id!, group.external_id, base64Data);
          
          // Após sucesso, baixa e salva a nova foto no Storage
          await handleRefreshGroupPhoto();
          toast.success('Foto do grupo alterada com sucesso!');
        } catch (error) {
          console.error('[GroupSidebar] Erro ao alterar foto:', error);
          toast.error('Erro ao alterar foto do grupo');
        } finally {
          setUploadingPhoto(false);
        }
      };
      
      reader.onerror = () => {
        toast.error('Erro ao processar imagem');
        setUploadingPhoto(false);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('[GroupSidebar] Erro ao processar arquivo:', error);
      toast.error('Erro ao processar imagem');
      setUploadingPhoto(false);
    }
    
    // Limpa input para permitir reupload do mesmo arquivo
    event.target.value = '';
  };

  return (
    <div className="w-full md:w-80 h-full bg-card flex flex-col rounded-lg shadow-sm border overflow-hidden">
      {/* Header - Responsivo para mobile */}
      <div className="h-16 px-4 flex items-center justify-between rounded-t-lg border-b md:border-b-0">
        <div className="flex items-center gap-2">
          {/* Botão voltar - visível apenas em mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden h-8 w-8"
            onClick={onClose}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h3 className="font-semibold">Informações do Grupo</h3>
        </div>
        {/* Botão X - visível apenas em desktop */}
        <Button variant="ghost" size="icon" onClick={onClose} className="hidden md:inline-flex">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/*
        Radix ScrollArea usa display:table no Viewport interno, fazendo o conteúdo
        crescer além de w-80. Forçamos block + w-full no Viewport para que
        min-w-0/flex-1 dos filhos passem a respeitar a largura do sidebar.
      */}
      <ScrollArea className="flex-1 [&>div>div]:!block [&>div>div]:!w-full">
        <div className="p-4 space-y-6 w-full max-w-full overflow-hidden">
          {/* Group Profile */}
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <Avatar className="h-20 w-20 mb-3">
                <AvatarImage src={getStorageUrl(groupPhoto)} />
                <AvatarFallback className="bg-blue-100 text-blue-600 text-2xl">
                  <Users className="h-10 w-10" />
                </AvatarFallback>
              </Avatar>
              {/* Botões sobrepostos: atualizar (esquerda) e upload (direita) */}
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                {/* Botão para atualizar foto do grupo (busca do WhatsApp) */}
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-7 w-7 rounded-full shadow-md"
                  onClick={handleRefreshGroupPhoto}
                  disabled={loadingPhoto || uploadingPhoto}
                  title="Baixar foto do WhatsApp"
                >
                  {loadingPhoto ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5" />
                  )}
                </Button>
                {/* Botão para upload de nova foto */}
                <label>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUploadGroupPhoto}
                    disabled={uploadingPhoto || loadingPhoto}
                  />
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7 rounded-full shadow-md cursor-pointer"
                    disabled={uploadingPhoto || loadingPhoto}
                    title="Enviar nova foto"
                    asChild
                  >
                    <span>
                      {uploadingPhoto ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Upload className="h-3.5 w-3.5" />
                      )}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
            <h4 className="font-semibold text-lg">{group.name}</h4>
            <Badge variant="secondary" className="mt-2">
              <Users className="h-3 w-3 mr-1" />
              {participants.length > 0 ? participants.length : group.participant_count} participantes
            </Badge>
          </div>

          <Separator />

          {/* Group Details */}
          <div className="space-y-4">
            <h5 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Detalhes
            </h5>

            {group.description && (
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-muted p-2 shrink-0">
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
                {/* min-w-0 + flex-1 evitam que URLs longas estourem o sidebar */}
                <div className="min-w-0 flex-1 w-full overflow-hidden">
                  <p className="text-xs text-muted-foreground">Descrição</p>
                  <p
                    className={cn(
                      // break-all força quebra mesmo em URLs sem espaços
                      "text-sm whitespace-pre-wrap break-all",
                      // Colapsado: limita a 3 linhas; expandido: mostra tudo
                      !isDescriptionExpanded && "line-clamp-3"
                    )}
                  >
                    {group.description}
                  </p>
                  {/* Botão Mostrar mais/menos só aparece em descrições longas */}
                  {group.description.length > 120 && (
                    <button
                      type="button"
                      onClick={() => setIsDescriptionExpanded((v) => !v)}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      {isDescriptionExpanded ? (
                        <>
                          Mostrar menos
                          <ChevronUp className="h-3 w-3" />
                        </>
                      ) : (
                        <>
                          Mostrar mais
                          <ChevronDown className="h-3 w-3" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-muted p-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">ID do Grupo</p>
                <p className="text-sm font-mono truncate">{group.external_id}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-muted p-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Conversa criada em</p>
                <p className="text-sm font-medium">
                  {format(new Date(conversation.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Distribuição Automática */}
          <Card className="border-dashed">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                Distribuição Automática
                {config?.autoDistributionEnabled && (
                  <Badge variant="secondary" className="text-xs ml-auto">
                    Ativa
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingConfig ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Toggle principal */}
                  <div className="flex items-center justify-between">
                    <Label htmlFor="auto-dist" className="text-sm">
                      Ativar distribuição
                    </Label>
                    <Switch
                      id="auto-dist"
                      checked={config?.autoDistributionEnabled || false}
                      onCheckedChange={handleToggleDistribution}
                      disabled={saving}
                    />
                  </div>

                  {config?.autoDistributionEnabled && (
                    <>
                      {/* Seleção de departamento */}
                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5" />
                          Departamento
                        </Label>
                        <Select
                          value={config.defaultDepartmentId || 'none'}
                          onValueChange={handleDepartmentChange}
                          disabled={saving}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Fila Geral" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Fila Geral</SelectItem>
                            {departments.map(dept => (
                              <SelectItem key={dept.id} value={dept.id}>
                                {dept.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Conversas serão encaminhadas para este departamento
                        </p>
                      </div>

                      {/* Trigger de distribuição */}
                      <div className="space-y-2">
                        <Label className="text-sm">Quando distribuir</Label>
                        <RadioGroup
                          value={config.distributionTrigger}
                          onValueChange={handleTriggerChange}
                          disabled={saving}
                          className="space-y-2"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="first_message" id="first" />
                            <Label htmlFor="first" className="text-xs font-normal cursor-pointer">
                              Apenas primeira mensagem
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="every_message" id="every" />
                            <Label htmlFor="every" className="text-xs font-normal cursor-pointer">
                              Toda mensagem (se sem agente)
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Info de última distribuição */}
                      {config.lastDistributedAt && (
                        <p className="text-xs text-muted-foreground">
                          Última distribuição: {format(new Date(config.lastDistributedAt), "dd/MM 'às' HH:mm", { locale: ptBR })}
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Separator />

          {/* Participants */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Participantes ({participants.length})
              </h5>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSyncParticipants(true)}
                disabled={loadingParticipants}
                title={fromCache ? "Atualizar (dados em cache)" : "Atualizar participantes"}
              >
                {loadingParticipants ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
              </Button>
            </div>

            {loadingParticipants ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : participants.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhum participante encontrado
              </p>
            ) : (
              <div className="space-y-2">
                {participants.map(participant => {
                  // Resolve nome priorizando contato CRM cadastrado
                  const phoneKey = (participant.participant_phone || '').replace(/\D/g, '');
                  const crmName = phoneKey ? contactsByPhone.get(phoneKey) : undefined;
                  const primaryName = crmName || participant.display_name || participant.participant_phone;
                  const secondaryLine = crmName
                    ? (participant.display_name && participant.display_name !== crmName
                        ? `${participant.display_name} · ${participant.participant_phone}`
                        : participant.participant_phone)
                    : participant.participant_phone;

                  return (
                    <div
                      key={participant.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className={cn("text-xs", getAvatarColorClasses(primaryName))}>
                          {getInitials(primaryName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{primaryName}</p>
                        <p className="text-xs text-muted-foreground truncate">{secondaryLine}</p>
                      </div>
                      {participant.is_super_admin && (
                        <Badge variant="secondary" className="text-xs">
                          <Crown className="h-3 w-3 mr-1" />
                          Criador
                        </Badge>
                      )}
                      {participant.is_admin && !participant.is_super_admin && (
                        <Badge variant="outline" className="text-xs">
                          <Shield className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Separator />

          {/* Statistics */}
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Estatísticas
            </h5>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold text-primary">
                  {conversation.unread_count}
                </p>
                <p className="text-xs text-muted-foreground">Não lidas</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold text-primary">
                  {conversation.last_message_at 
                    ? format(new Date(conversation.last_message_at), 'HH:mm') 
                    : '—'}
                </p>
                <p className="text-xs text-muted-foreground">Última msg</p>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
